<template>
  <!--
    AB#21966 — the team chat composer: @mentions, files, the portion a message
    is about, and Write / Rewrite / Translate as a suggestion to use or discard.
    Enter sends, Shift+Enter is a new line.
  -->
  <div class="tcc" data-test="tc-composer">
    <div v-if="suggestion" class="tcc-sug" data-test="tc-suggestion">
      <div class="tcc-sug-h">
        <SparklesIcon class="size-3.5" aria-hidden="true" />
        {{ suggestion.title }}
      </div>
      <p class="tcc-sug-t">{{ suggestion.text }}</p>
      <div class="tcc-sug-a">
        <button
          type="button"
          class="tcc-chip"
          data-test="tc-suggestion-discard"
          @click="suggestion = null"
        >
          Discard
        </button>
        <button
          type="button"
          class="tcc-chip tcc-chip--primary"
          data-test="tc-suggestion-use"
          @click="useSuggestion"
        >
          Use this
        </button>
      </div>
    </div>
    <p v-if="assistError" class="tcc-err" role="alert">{{ assistError }}</p>

    <div v-if="replyTo" class="tcc-reply" data-test="tc-replying">
      <span
        >Replying to <b>{{ replyTo.kind === 'elsa' ? 'ELSA' : replyTo.user?.name }}</b
        >: {{ excerpt(replyTo) }}</span
      >
      <button type="button" class="tcc-x" aria-label="Cancel reply" @click="emit('cancel-reply')">
        <XMarkIcon class="size-4" />
      </button>
    </div>

    <div
      class="tcc-box"
      :class="{ 'tcc-box--drag': dragging }"
      @dragover.prevent="dragging = true"
      @dragleave="dragging = false"
      @drop.prevent="onDrop"
    >
      <div v-if="files.length" class="tcc-files">
        <span
          v-for="(f, i) in files"
          :key="`${f.name}-${i}`"
          class="tcc-fchip"
          :data-test="`tc-pending-file-${i}`"
        >
          <PaperClipIcon class="size-3.5" aria-hidden="true" />
          <span class="tcc-fname">{{ f.name }}</span>
          <span class="tcc-fsize">{{ fileSize(f.size) }}</span>
          <button
            type="button"
            class="tcc-x"
            :aria-label="`Remove ${f.name}`"
            @click="files.splice(i, 1)"
          >
            <XMarkIcon class="size-3.5" />
          </button>
        </span>
      </div>
      <label :for="inputId" class="sr-only">Message</label>
      <textarea
        :id="inputId"
        ref="input"
        v-model="text"
        class="tcc-input"
        rows="2"
        :placeholder="placeholder"
        :disabled="sending"
        data-test="tc-input"
        @input="onInput"
        @keydown="onKeydown"
      ></textarea>

      <div
        v-if="mention.open && mention.people.length"
        class="tcc-mentions"
        role="listbox"
        data-test="tc-mentions"
      >
        <button
          v-for="(p, i) in mention.people"
          :key="p.id"
          type="button"
          role="option"
          class="tcc-mention"
          :class="{ 'tcc-mention--on': i === mention.index }"
          :aria-selected="i === mention.index"
          :data-test="`tc-mention-${p.id}`"
          @mousedown.prevent="pickMention(p)"
        >
          <UserAvatar
            :name="p.name"
            :initials="p.initials"
            :src="p.avatar_url ?? null"
            :size="22"
            tone="soft"
          />
          <span>{{ p.name }}</span>
          <span v-if="p.role" class="tcc-mention-role">{{ p.role }}</span>
        </button>
      </div>

      <div class="tcc-bar">
        <input
          :id="`${inputId}-files`"
          ref="fileInput"
          type="file"
          multiple
          class="sr-only"
          data-test="tc-file-input"
          @change="onPick"
        />
        <button
          type="button"
          class="tcc-chip"
          aria-label="Attach files"
          title="Attach files"
          @click="fileInput?.click()"
        >
          <PaperClipIcon class="size-4" />
        </button>
        <button
          type="button"
          class="tcc-chip"
          aria-label="Mention someone"
          title="Mention someone"
          @click="startMention"
        >
          @
        </button>
        <select
          v-if="portions.length"
          v-model="portionId"
          class="tcc-select"
          aria-label="About which portion"
          data-test="tc-portion"
        >
          <option :value="null">Whole order</option>
          <option v-for="p in portions" :key="p.id" :value="p.id">
            {{ p.item_no }}{{ p.factory ? ` · ${p.factory}` : '' }}
          </option>
        </select>
        <template v-if="canWrite || canTranslate">
          <span class="tcc-sep" aria-hidden="true"></span>
          <button
            v-if="canWrite"
            type="button"
            class="tcc-chip"
            :disabled="busy || !text.trim()"
            title="Turn a few words into a message"
            data-test="tc-write"
            @click="run('write')"
          >
            <SparklesIcon class="size-3.5" />Write
          </button>
          <div v-if="canWrite" class="tcc-menu-wrap">
            <button
              type="button"
              class="tcc-chip"
              :disabled="busy || !text.trim()"
              :aria-expanded="menuOpen"
              data-test="tc-rewrite"
              @click="menuOpen = !menuOpen"
            >
              Rewrite<ChevronDownIcon class="size-3" />
            </button>
            <div v-if="menuOpen" class="tcc-menu" role="menu" data-test="tc-rewrite-menu">
              <button
                v-for="o in REWRITE_OPTIONS"
                :key="o.action"
                type="button"
                role="menuitem"
                class="tcc-menu-item"
                :data-test="`tc-rewrite-${o.action}`"
                @click="run(o.action)"
              >
                {{ o.label }}
              </button>
            </div>
          </div>
          <button
            v-if="canTranslate && nonEnglish"
            type="button"
            class="tcc-chip tcc-chip--warn"
            :disabled="busy"
            data-test="tc-translate-draft"
            @click="run('translate')"
          >
            <LanguageIcon class="size-3.5" />Translate to English
          </button>
        </template>
        <span class="tcc-grow"></span>
        <span v-if="busy" class="tcc-hint">Working…</span>
        <span v-else class="tcc-hint tcc-hint--wide">Enter to send · Shift+Enter new line</span>
        <button
          type="button"
          class="tcc-send"
          :disabled="sending || (!text.trim() && !files.length)"
          data-test="tc-send"
          @click="submit"
        >
          <PaperAirplaneIcon class="size-4" />{{ sending ? 'Sending…' : 'Send' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import {
  ChevronDownIcon,
  LanguageIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import {
  REWRITE_OPTIONS,
  assist,
  fileSize,
  looksNonEnglish,
  mentionIdsIn,
  searchPeople,
  type TcAssistAction,
  type TcMessage,
  type TcPerson,
  type TcPortion,
} from '@/composables/useTeamChat';

const props = withDefaults(
  defineProps<{
    orderNo: string;
    sending?: boolean;
    replyTo?: TcMessage | null;
    portions?: TcPortion[];
    canWrite?: boolean;
    canTranslate?: boolean;
    maxFileMb?: number;
  }>(),
  {
    sending: false,
    replyTo: null,
    portions: () => [],
    canWrite: false,
    canTranslate: false,
    maxFileMb: 25,
  }
);
const emit = defineEmits<{
  (
    e: 'send',
    payload: {
      body: string;
      files: File[];
      replyToId: number | null;
      portionId: number | null;
      mentions: number[];
    }
  ): void;
  (e: 'typing'): void;
  (e: 'cancel-reply'): void;
}>();

const inputId = `tc-input-${Math.random().toString(36).slice(2, 8)}`;
const input = ref<HTMLTextAreaElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const text = ref('');
const files = ref<File[]>([]);
const portionId = ref<number | null>(null);
const dragging = ref(false);
const menuOpen = ref(false);
const busy = ref(false);
const assistError = ref<string | null>(null);
const suggestion = ref<{ title: string; text: string } | null>(null);
const picked = ref<TcPerson[]>([]);
const mention = reactive({ open: false, query: '', start: -1, people: [] as TcPerson[], index: 0 });

const nonEnglish = computed(() => looksNonEnglish(text.value));
const placeholder = computed(() => `Message the team about #${props.orderNo}… (@ to mention)`);
const excerpt = (m: TcMessage): string => {
  const t = (m.body_en || m.body || '').replace(/\s+/g, ' ');
  return t.length > 90 ? `${t.slice(0, 90)}…` : t;
};

watch(
  () => props.replyTo,
  (r) => {
    if (r) input.value?.focus();
  }
);

const addFiles = (list: FileList | File[] | null): void => {
  assistError.value = null;
  for (const f of Array.from(list ?? [])) {
    if (f.size > props.maxFileMb * 1024 * 1024) {
      assistError.value = `${f.name} is larger than ${props.maxFileMb} MB.`;
      continue;
    }
    if (files.value.length < 10) files.value.push(f);
  }
};
const onPick = (e: Event): void => {
  const el = e.target as HTMLInputElement;
  addFiles(el.files);
  el.value = '';
};
const onDrop = (e: DragEvent): void => {
  dragging.value = false;
  addFiles(e.dataTransfer?.files ?? null);
};

/* ---- @mentions ---- */
let searchSeq = 0;
const findMentionQuery = (): void => {
  const el = input.value;
  const pos = el?.selectionStart ?? text.value.length;
  const before = text.value.slice(0, pos);
  const m = before.match(/(^|\s)@([\p{L}][\p{L} ]{0,30})?$/u);
  if (!m) {
    mention.open = false;
    return;
  }
  mention.open = true;
  mention.query = (m[2] ?? '').trimStart();
  mention.start = pos - (m[2]?.length ?? 0) - 1;
  const seq = ++searchSeq;
  void searchPeople(mention.query).then((people) => {
    if (seq !== searchSeq) return;
    mention.people = people.slice(0, 8);
    mention.index = 0;
  });
};
const startMention = (): void => {
  const el = input.value;
  const pos = el?.selectionStart ?? text.value.length;
  const pad = pos > 0 && !/\s/.test(text.value[pos - 1]) ? ' ' : '';
  text.value = `${text.value.slice(0, pos)}${pad}@${text.value.slice(pos)}`;
  requestAnimationFrame(() => {
    el?.focus();
    const at = pos + pad.length + 1;
    el?.setSelectionRange(at, at);
    findMentionQuery();
  });
};
const pickMention = (p: TcPerson): void => {
  const el = input.value;
  const pos = el?.selectionStart ?? text.value.length;
  text.value = `${text.value.slice(0, mention.start)}@${p.name} ${text.value.slice(pos)}`;
  if (!picked.value.some((x) => x.id === p.id)) picked.value.push(p);
  mention.open = false;
  requestAnimationFrame(() => {
    const at = mention.start + p.name.length + 2;
    el?.focus();
    el?.setSelectionRange(at, at);
  });
};

const onInput = (): void => {
  emit('typing');
  findMentionQuery();
};
const onKeydown = (e: KeyboardEvent): void => {
  if (mention.open && mention.people.length) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      mention.index = (mention.index + 1) % mention.people.length;
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      mention.index = (mention.index - 1 + mention.people.length) % mention.people.length;
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pickMention(mention.people[mention.index]);
      return;
    }
    if (e.key === 'Escape') {
      mention.open = false;
      return;
    }
  }
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault();
    submit();
  }
};

/* ---- Write / Rewrite / Translate ---- */
const TITLES: Record<TcAssistAction, string> = {
  write: 'Write',
  clearer: 'Rewrite · Clearer',
  shorter: 'Rewrite · Shorter',
  friendlier: 'Rewrite · Friendlier',
  formal: 'Rewrite · More formal',
  fix: 'Rewrite · Fix spelling & grammar',
  translate: 'Translated to English',
};
const run = async (action: TcAssistAction): Promise<void> => {
  menuOpen.value = false;
  const t = text.value.trim();
  if (!t || busy.value) return;
  busy.value = true;
  assistError.value = null;
  try {
    const r = await assist(action, t, props.orderNo);
    suggestion.value = {
      title:
        action === 'translate' && r.source_language
          ? `Translated to English · from ${r.source_language}`
          : TITLES[action],
      text: r.text,
    };
  } catch (e) {
    assistError.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
};
const useSuggestion = (): void => {
  if (!suggestion.value) return;
  text.value = suggestion.value.text;
  suggestion.value = null;
  input.value?.focus();
};

const submit = (): void => {
  const body = text.value.trim();
  if (props.sending || (!body && !files.value.length)) return;
  emit('send', {
    body,
    files: [...files.value],
    replyToId: props.replyTo?.id ?? null,
    portionId: portionId.value,
    mentions: mentionIdsIn(body, picked.value),
  });
};

/** Called by the tab once the message is accepted. */
const reset = (): void => {
  text.value = '';
  files.value = [];
  picked.value = [];
  suggestion.value = null;
  assistError.value = null;
};
/**
 * AB#22133 — start the box with a drafted message and the people it mentions
 * (the CSM queue's Talk to). The person still reads, edits and sends it.
 */
const prefill = (body: string, people: TcPerson[] = []): void => {
  text.value = body;
  picked.value = [...people];
  void nextTick(() => {
    const el = input.value;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  });
};

/** AB#22134 — fit the box to its text (up to its max-height), so a long draft shows whole. */
const grow = (): void => {
  const el = input.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight + 2, 260)}px`;
};
watch(text, () => void nextTick(grow));
defineExpose({ reset, prefill, focus: () => input.value?.focus() });
</script>

<style scoped>
.tcc {
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}
.tcc-box {
  border: 1px solid var(--border);
  border-radius: 11px;
  background: var(--surface);
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
}
.tcc-box:focus-within {
  border-color: var(--accent);
}
.tcc-box--drag {
  border-style: dashed;
  border-color: var(--accent);
  background: var(--accent-softer, var(--surface-2));
}
.tcc-input {
  width: 100%;
  min-height: 44px;
  /* AB#22134 — the box grows with what is in it (a drafted message from the
     CSM queue is several lines), up to this, then scrolls. */
  max-height: 260px;
  resize: vertical;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
}
.tcc-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.tcc-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--ink-soft);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.tcc-chip:hover:not(:disabled) {
  border-color: var(--accent);
  color: var(--ink);
}
.tcc-chip:disabled {
  opacity: 0.5;
  cursor: default;
}
.tcc-chip--primary {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.tcc-chip--warn {
  border-color: color-mix(in srgb, var(--warning) 45%, var(--border));
  color: var(--warning);
}
.tcc-select {
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--ink-soft);
  font-size: 12px;
  padding: 0 8px;
  max-width: 180px;
}
.tcc-sep {
  width: 1px;
  height: 20px;
  background: var(--border);
  margin: 0 2px;
}
.tcc-grow {
  flex: 1;
}
.tcc-hint {
  font-size: 11px;
  color: var(--ink-mute);
}
@media (max-width: 700px) {
  .tcc-hint--wide {
    display: none;
  }
}
.tcc-send {
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  font-size: 12.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}
.tcc-send:disabled {
  opacity: 0.5;
  cursor: default;
}
.tcc-menu-wrap {
  position: relative;
}
.tcc-menu {
  position: absolute;
  bottom: 36px;
  left: 0;
  width: 210px;
  padding: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  box-shadow: var(--shadow-2, 0 16px 40px rgba(0, 0, 0, 0.3));
  z-index: 20;
}
.tcc-menu-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-size: 12.5px;
  border-radius: 6px;
  cursor: pointer;
}
.tcc-menu-item:hover {
  background: var(--surface-2);
}
.tcc-mentions {
  position: absolute;
  left: 10px;
  bottom: calc(100% + 4px);
  width: min(320px, 90%);
  max-height: 260px;
  overflow: auto;
  padding: 6px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  box-shadow: var(--shadow-2, 0 16px 40px rgba(0, 0, 0, 0.3));
  z-index: 20;
}
.tcc-mention {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-size: 12.5px;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
}
.tcc-mention--on,
.tcc-mention:hover {
  background: var(--surface-2);
}
.tcc-mention-role {
  margin-left: auto;
  font-size: 11px;
  color: var(--ink-mute);
}
.tcc-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.tcc-fchip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 240px;
  height: 26px;
  padding: 0 4px 0 8px;
  border-radius: 7px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  font-size: 11.5px;
  color: var(--ink);
}
.tcc-fname {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcc-fsize {
  color: var(--ink-mute);
  white-space: nowrap;
}
.tcc-x {
  border: 0;
  background: transparent;
  color: var(--ink-mute);
  display: inline-flex;
  padding: 2px;
  border-radius: 4px;
  cursor: pointer;
}
.tcc-x:hover {
  color: var(--ink);
}
.tcc-reply {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--ink-soft);
  border-left: 2px solid var(--accent);
  padding: 2px 0 2px 8px;
}
.tcc-reply span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tcc-sug {
  border: 1px solid color-mix(in srgb, var(--accent) 40%, var(--border));
  background: var(--accent-softer, var(--surface-2));
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tcc-sug-h {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
}
.tcc-sug-t {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ink);
  white-space: pre-wrap;
}
.tcc-sug-a {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.tcc-err {
  margin: 0;
  font-size: 12px;
  color: var(--danger);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
</style>
